export const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

export const pad = (value: number) => String(value).padStart(2, "0");

export const addDaysToDate = (
  days: number,
  startDate: Date = new Date(),
): Date => {
  const newDate = new Date(startDate);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

const getDateOnlyComparisonValue = (date: string | Date): number | null => {
  if (date instanceof Date) {
    if (!isValidDate(date)) return null;

    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
    );
  }

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const parsedDate = new Date(`${year}-${month}-${day}T00:00:00Z`);
    if (!isValidDate(parsedDate)) return null;

    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const parsedDate = new Date(date);
  if (!isValidDate(parsedDate)) return null;

  return Date.UTC(
    parsedDate.getUTCFullYear(),
    parsedDate.getUTCMonth(),
    parsedDate.getUTCDate(),
  );
};

export const isDateExpired = (date: string | Date): boolean => {
  const dateToCheck = getDateOnlyComparisonValue(date);
  if (dateToCheck === null) return false;

  const today = new Date();
  const todayDateOnly = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return dateToCheck < todayDateOnly;
};

export const toUtcDateParts = (date: Date) => {
  if (!(date instanceof Date) || !isValidDate(date)) return null;

  return {
    year: String(date.getUTCFullYear()),
    month: String(date.getUTCMonth() + 1).padStart(2, "0"),
    day: String(date.getUTCDate()).padStart(2, "0"),
  };
};

export const parseDateOnlyParts = (input: string | number | Date) => {
  if (typeof input === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
    if (match) {
      const [, year, month, day] = match;
      return {
        year,
        month,
        day,
      };
    }
  }

  const parsedDate = new Date(input);
  if (!isValidDate(parsedDate)) return null;

  return toUtcDateParts(parsedDate);
};

export const formatNativeDateInputValue = (
  date: Date | string | null | undefined,
): string => {
  if (!date) return "";

  if (typeof date === "string") {
    const parts = parseDateOnlyParts(date);
    if (!parts) return "";

    const parsedDate = new Date(
      `${parts.year}-${parts.month}-${parts.day}T00:00:00Z`,
    );
    if (!isValidDate(parsedDate)) return "";

    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  const parts = toUtcDateParts(date);
  if (!parts) return "";

  return `${parts.year}-${parts.month}-${parts.day}`;
};
